import {ActionIcon, Tooltip} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useChecklistData} from "./ChecklistDataContext";
import {Status} from "../types";
import ItemCheckIcon from "../../../assets/v2-item-check.svg?react";
import AlertCircleIcon from "../../../assets/v2-alert-circle.svg?react";
import ItemNaIcon from "../../../assets/v2-item-na.svg?react";
import styles from "./CheckStatusButtons.module.css";

const STATUS_CONFIG = [
    {
        status: Status.ok,
        Icon: ItemCheckIcon,
        color: "green",
        activeColor: "var(--mantine-color-green-6)",
        inactiveColor: "var(--mantine-color-green-3)",
    },
    {
        status: Status.deviation,
        Icon: AlertCircleIcon,
        color: "orange",
        activeColor: "var(--mantine-color-orange-5)",
        inactiveColor: "var(--mantine-color-orange-3)",
    },
    {
        status: Status.na,
        Icon: ItemNaIcon,
        color: "red",
        activeColor: "var(--mantine-color-red-5)",
        inactiveColor: "var(--mantine-color-red-3)",
    },
] as const;

export const CheckStatusButtons = ({
    itemId,
    onStatusClick,
    names,
    isGroup,
}: {
    itemId: string;
    onStatusClick: (status: Status) => void;
    names: Record<Status, string>;
    isGroup?: boolean;
}) => {
    const {t} = useTranslation();
    const {getStatus} = useChecklistData();
    const currentStatus = getStatus(itemId);

    const handleClick = (status: Status) => {
        onStatusClick(currentStatus === status ? Status.todo : status);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const currentIdx = STATUS_CONFIG.findIndex(
            (c) => c.status === currentStatus,
        );
        let nextIdx = -1;

        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            nextIdx = (currentIdx + 1) % STATUS_CONFIG.length;
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            nextIdx =
                (currentIdx - 1 + STATUS_CONFIG.length) %
                STATUS_CONFIG.length;
        }

        if (nextIdx !== -1) {
            onStatusClick(STATUS_CONFIG[nextIdx].status);
        }
    };

    return (
        <div
            className={styles.statusButtons}
            role="radiogroup"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
        >
            {STATUS_CONFIG.map(({status, Icon, activeColor, inactiveColor}) => {
                const isActive = currentStatus === status;
                const label = isGroup
                    ? t("Label.MarkAllAsStatus", {status: names[status].toLowerCase()})
                    : names[status];

                return (
                    <Tooltip key={status} label={label} openDelay={400}>
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            color={isActive ? activeColor : inactiveColor}
                            aria-checked={isActive}
                            role="radio"
                            onClick={() => handleClick(status)}
                            className={
                                isActive ? styles.activeButton : undefined
                            }
                        >
                            <Icon width={16} height={16} />
                        </ActionIcon>
                    </Tooltip>
                );
            })}
        </div>
    );
};
